import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import LanguageToggle from "@/components/LanguageToggle";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "", access_code: "" });
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register(form);
      toast.success(t("common.success"));
      nav("/app");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] grain-bg flex flex-col" data-testid="register-page">
      <header className="container mx-auto px-4 md:px-8 py-6 flex items-center justify-between">
        <Logo />
        <LanguageToggle />
      </header>
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md bg-white rounded-xl border border-[#E5E2D9] shadow-sm p-8 fade-up">
          <h1 className="font-display font-bold text-3xl tracking-tight text-[#1A2F24] mb-1">
            {t("auth.register_title")}
          </h1>
          <p className="text-sm text-[#5C6B61] mb-7">{t("auth.register_subtitle")}</p>
          <form onSubmit={submit} className="space-y-4">
            <div className="rounded-md border border-[#E5E2D9] bg-[#F1EFE7] p-4" data-testid="register-access-code-block">
              <Label htmlFor="access_code">{t("auth.access_code")}</Label>
              <Input
                id="access_code"
                required
                autoComplete="off"
                spellCheck={false}
                placeholder={t("auth.access_code_placeholder")}
                data-testid="register-access-code-input"
                value={form.access_code}
                onChange={(e) => setForm({ ...form, access_code: e.target.value.toUpperCase() })}
                className="mt-1.5 bg-white border-[#E5E2D9] uppercase tracking-widest font-medium"
              />
              <p className="text-xs text-[#5C6B61] mt-2">{t("auth.access_code_hint")}</p>
            </div>
            <div>
              <Label htmlFor="name">{t("auth.name")}</Label>
              <Input id="name" required data-testid="register-name-input" value={form.name} onChange={set("name")} className="mt-1.5 bg-[#F9F8F6] border-[#E5E2D9]" />
            </div>
            <div>
              <Label htmlFor="company">{t("auth.company")}</Label>
              <Input id="company" data-testid="register-company-input" value={form.company} onChange={set("company")} className="mt-1.5 bg-[#F9F8F6] border-[#E5E2D9]" />
            </div>
            <div>
              <Label htmlFor="email">{t("auth.email")}</Label>
              <Input id="email" type="email" required autoComplete="email" data-testid="register-email-input" value={form.email} onChange={set("email")} className="mt-1.5 bg-[#F9F8F6] border-[#E5E2D9]" />
            </div>
            <div>
              <Label htmlFor="password">{t("auth.password")}</Label>
              <Input id="password" type="password" required minLength={6} autoComplete="new-password" data-testid="register-password-input" value={form.password} onChange={set("password")} className="mt-1.5 bg-[#F9F8F6] border-[#E5E2D9]" />
            </div>
            {err && (
              <div data-testid="register-error" className="text-sm text-[#C05A44] bg-[#FDF2EE] border border-[#F2D9D0] rounded-md p-3">
                {err}
              </div>
            )}
            <Button type="submit" data-testid="register-submit-button" disabled={loading} className="w-full h-11 bg-[#1A2F24] hover:bg-[#2E3B32] text-white">
              {loading ? t("common.loading") : t("auth.register_button")}
            </Button>
          </form>
          <p className="text-sm text-[#5C6B61] mt-6 text-center">
            {t("auth.have_account")}{" "}
            <Link to="/login" className="text-[#1A2F24] font-medium hover:underline" data-testid="link-to-login">
              {t("auth.to_login")}
            </Link>
          </p>
          <p className="text-xs text-[#5C6B61] mt-3 text-center" data-testid="register-legal-consent">
            Mit der Registrierung akzeptieren Sie unsere{" "}
            <Link to="/datenschutz" className="text-[#1A2F24] hover:underline">Datenschutzerklärung</Link>.
          </p>
          <div className="flex justify-center gap-4 mt-4 text-xs text-[#5C6B61]">
            <Link to="/impressum" className="hover:text-[#1A2F24]" data-testid="register-impressum-link">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-[#1A2F24]" data-testid="register-datenschutz-link">Datenschutz</Link>
          </div>
        </div>
      </main>
    </div>
  );
}
